import { z } from 'zod'
import type { ContractSourceMeta } from './questionnaire'

export const AnalyticsMetricKeySchema = z.enum([
  'impressions',
  'starts',
  'completions',
  'completionRate',
  'averageTimeSeconds',
])
export type AnalyticsMetricKey = z.infer<typeof AnalyticsMetricKeySchema>

export type AnalyticsMetricContract = {
  key: AnalyticsMetricKey
  label: string
  value: number
  unit: 'count' | 'percent' | 'seconds'
  changePercent: number
  sourceMeta: ContractSourceMeta
}

export type TrendPointContract = {
  date: string
  impressions: number
  starts: number
  completions: number
}

export type QuestionAnalyticsContract = {
  questionId: string
  questionTitle: string
  questionType: string
  totalAnswers: number
  distribution: Array<{
    label: string
    value: number
    percent: number
  }>
  sourceMeta: ContractSourceMeta
}

export type FunnelStepContract = {
  questionId: string
  label: string
  reached: number
  dropped: number
  dropoffRate: number
}

export type TrafficSourceContract = {
  source: 'direct' | 'social' | 'email' | 'embed' | 'ad' | 'other'
  label: string
  impressions: number
  starts: number
  completions: number
  completionRate: number
}

export type DeviceAnalyticsContract = {
  device: 'desktop' | 'mobile' | 'tablet'
  label: string
  visitors: number
  completionRate: number
  averageTimeSeconds: number
}

export type AnalyticsEventContract = {
  id: string
  eventType:
    | 'form_viewed'
    | 'form_started'
    | 'question_viewed'
    | 'question_answered'
    | 'form_submitted'
    | 'form_abandoned'
  occurredAt: string
  questionId: string | null
  responseLinked: boolean
  visitorLinked: boolean
  sourceMeta: ContractSourceMeta
}

export type CrossAnalysisSegmentContract = {
  id: string
  segmentLabel: string
  baseQuestionTitle: string
  compareQuestionTitle: string
  responses: number
  completionRate: number
  averageScore: number
  insight: string
}

export type CrossAnalysisContract = {
  formId: string
  title: string
  segments: CrossAnalysisSegmentContract[]
  sourceMeta: ContractSourceMeta
}

export type VisitorRecordContract = {
  id: string
  visitedAt: string
  sourceLabel: string
  utm: {
    source: string
    medium: string
    campaign: string
  }
  salesCode: string
  campaignCode: string
  deviceLabel: string
  browser: string
  country: string
  city: string
  sessionDurationSeconds: number
  started: boolean
  completed: boolean
  hiddenFields: Record<string, string>
  sourceMeta: ContractSourceMeta
}

export type ExportFormatContract = 'csv' | 'xlsx' | 'json' | 'spss' | 'raw_package'

export type ExportContractPreview = {
  format: ExportFormatContract
  label: string
  filename: string
  contentType: string
  jobMode: 'mock_contract_preview'
  providerState: 'not_configured'
  estimatedRows: number
  selectedFieldCount: number
  dateRange: {
    from: string
    to: string
  }
  filters: Array<{
    key: string
    label: string
    value: string
  }>
  permission: {
    requiredRole: 'admin' | 'manager' | 'viewer'
    note: string
  }
  includes: string[]
  warning: string
  sourceMeta: ContractSourceMeta
}

export type AnalyticsDashboardContract = {
  metrics: AnalyticsMetricContract[]
  trend: TrendPointContract[]
  questionAnalytics: QuestionAnalyticsContract[]
  funnel: FunnelStepContract[]
  sources: TrafficSourceContract[]
  devices: DeviceAnalyticsContract[]
  crossAnalysis: CrossAnalysisContract
  visitors: VisitorRecordContract[]
  sourceMeta: ContractSourceMeta
}

export const AnalyticsRangeParamsSchema = z.object({
  formId: z.string().optional(),
  range: z.enum(['7d', '30d', '90d']).default('30d'),
})
export type AnalyticsRangeParams = z.input<typeof AnalyticsRangeParamsSchema>

export const exportFormatOptions: Array<{
  value: ExportFormatContract
  label: string
  description: string
}> = [
  { value: 'csv', label: 'CSV', description: 'contract preview；未接線前不產生真實 CSV。' },
  {
    value: 'xlsx',
    label: 'Excel',
    description: 'contract preview；未接線前不產生真實 xlsx，且未安裝 xlsx runtime。',
  },
  { value: 'json', label: 'JSON', description: 'contract preview；未接線前不產生真實 JSON 檔案。' },
  { value: 'spss', label: 'SPSS 語法包', description: 'contract preview；未接線前不產生真實語法包。' },
  {
    value: 'raw_package',
    label: '原始資料包',
    description: 'contract preview；未接線前不產生 raw package。',
  },
]
